import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPhoneInventory } from '../../../../store/trustHubSlice';
import { setMessage } from '../../../../store/Page';
import api from '../../../../api';
import axios from '../../../../config/axios';
import config from '../../../../config';
import PhoneInventoryList from '../components/phones/PhoneInventoryList';
import VerifiedCallerIdList from '../components/verifiedcallerids/VerifiedCallerIdList';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import './PhoneInventoryTab.css';

const PhoneInventoryTab = ({ user }) => {
  const dispatch = useDispatch();
  const { phoneInventory, phonePagination, phoneLoading, phoneError } = useSelector(
    (state) => state.trustHub
  );

  const userId = user?.user_id || user?.id;

  // Get Business profile from Redux store
  const trustHubProfile = useSelector(state => state.trustHub?.profile);

  // Get SMS compliance data for A2P assignment
  const smsCompliance = useSelector(state => state.trustHub?.smsCompliance);
  const campaignDataFromRedux = smsCompliance?.campaign;

  // Local state
  const [activeSubTab, setActiveSubTab] = useState('voice'); // 'voice' or 'verifiedcallerid'
  const [campaignData, setCampaignData] = useState(null);
  const [verifiedCallerIds, setVerifiedCallerIds] = useState([]);

  const fetchCampaignData = async () => {
    try {
      const response = await axios.get(`${config.apiBaseURL2}a2p/campaign/list/${userId}`);
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const approvedCampaign = response.data.data.find(c =>
          c.status === 'approved' || c.status === 'twilio-approved'
        );
        setCampaignData(approvedCampaign || campaignDataFromRedux);
      } else {
        setCampaignData(campaignDataFromRedux);
      }
    } catch (err) {
      console.error('Error fetching campaign data:', err);
      setCampaignData(campaignDataFromRedux);
    }
  };

  // Fetch campaign data on mount
  useEffect(() => {
    fetchCampaignData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Loading state
  if (phoneLoading && !phoneInventory.length) {
    return (
      <div className="phone-inventory-tab">
        <LoadingSkeleton variant="table" />
      </div>
    );
  }

  // Error state
  if (phoneError) {
    return (
      <div className="phone-inventory-tab">
        <div className="alert alert-danger">
          <h4>Error Loading Phone Inventory</h4>
          <p>{phoneError}</p>
          <button
            className="btn linear-gradient zy-save-btn ml-2 mt-3"
            onClick={() => dispatch(fetchPhoneInventory({ userId, params: {} }))}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="phone-inventory-tab">
      {/* Sub-Tabs for Voice, SMS, and Verified Caller IDs */}
      <div className="phone-inventory-subtabs">
        <button
          className={`subtab-btn ${activeSubTab === 'voice' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('voice')}
        >
         Business Profile Numbers
        </button>
        <button
          className={`subtab-btn ${activeSubTab === 'verifiedcallerid' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('verifiedcallerid')}
        >
         Verified Caller IDs
        </button>
      </div>

      {/* Voice Tab Content */}
      {activeSubTab === 'voice' && (
        <PhoneInventoryList
          campaignData={campaignData}
          onLoadVoicePhones={async (params) => {
            // Load phones directly from API
            try {
              const response = await api.trustHubCentralized.getUserPhones(userId, params);
              console.log('Voice phones API response:', response);

              if (response.success && response.data) {
                const phones = response.data.data || [];
                const pagination = {
                  current_page: response.data.current_page || 1,
                  last_page: response.data.last_page || 1,
                  per_page: response.data.per_page || 10,
                  total: response.data.total || 0,
                };

                return {
                  phones: phones,
                  totalCount: pagination.total,
                  summary: response.data.summary || null,
                };
              }

              return {
                phones: [],
                totalCount: 0,
                summary: null,
              };
            } catch (err) {
              console.error('Error loading voice phones:', err);
              return {
                phones: [],
                totalCount: 0,
              };
            }
          }}
          onAssignPhone={async (phone) => {
            const profileSid = trustHubProfile?.twilio_customer_profile_sid || trustHubProfile?.profile_sid;
            if (!profileSid) {
              throw new Error('Business profile not found');
            }
            const phoneSid = phone.phone_sid || phone.phone_number_sid;
            const response = await api.trustHubCentralized.assignPhoneProfile({
              user_id: userId,
              profile_sid: profileSid,
              phone_number_sid: phoneSid,
            });
            if (response?.success) {
              dispatch(setMessage({ type: 'success', text: `Phone ${phone.phone_number} assigned to Business profile` }));
            }
          }}
          onUnassignPhone={async (phone) => {
            const response = await api.trustHubCentralized.unassignPhone({
              user_id: userId,
              phone_id: phone.id,
              assignment_sid: phone.assignment_sid,
            });
            if (response?.success) {
              dispatch(setMessage({ type: 'success', text: `Phone ${phone.phone_number} unassigned from Business profile` }));
            }
          }}
          onBulkAssignPhones={async (phones) => {
            const profileSid = trustHubProfile?.twilio_customer_profile_sid || trustHubProfile?.profile_sid;
            if (!profileSid) {
              throw new Error('Business profile not found');
            }
            const phoneNumberSids = phones.map(p => p.phone_sid || p.phone_number_sid);
            const response = await api.trustHubCentralized.bulkAssignPhonesProfile({
              user_id: userId,
              profile_sid: profileSid,
              phone_number_sids: phoneNumberSids,
            });
            if (response?.success) {
              dispatch(setMessage({ type: 'success', text: `${phones.length} phone(s) assigned to Business profile` }));
            }
          }}
          onAssignPhoneToSms={async (phone) => {
            try {
              if (!campaignData?.id) {
                throw new Error('No active A2P campaign found. Please create and approve an A2P Campaign in SMS Compliance tab first.');
              }

              // API: POST /api/v2/a2p/campaign/assign-phone
              const response = await axios.post(`${config.apiBaseURL2}a2p/campaign/assign-phone`, {
                user_id: userId,
                a2p_campaign_id: campaignData.id,
                phone_number_sid: phone.phone_number_sid || phone.phone_sid
              });

              if (response.data.success) {
                dispatch(setMessage({
                  type: 'success',
                  text: response.data.message || `Phone ${phone.phone_number} assigned to SMS campaign successfully`
                }));
              } else {
                dispatch(setMessage({
                  type: 'error',
                  text: response.data.message || 'Failed to assign phone to SMS campaign'
                }));
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message
                || err.response?.data?.error
                || err.message
                || 'Failed to assign phone to SMS campaign';

              dispatch(setMessage({ type: 'error', text: errorMessage }));
              throw err;
            }
          }}
          onUnassignPhoneFromSms={async (phone) => {
            try {
              // Check if phone has A2P assignment ID
              const assignmentId = phone.a2p_assignment_id;

              if (!assignmentId) {
                throw new Error('Assignment ID not found. This phone may not be assigned to the SMS campaign.');
              }

              // API: DELETE /api/v2/a2p/campaign/remove-phone/{assignment_id}
              const response = await axios.delete(`${config.apiBaseURL2}a2p/campaign/remove-phone/${assignmentId}`);

              if (response.data.success) {
                dispatch(setMessage({
                  type: 'success',
                  text: response.data.message || `Phone ${phone.phone_number} unassigned from SMS campaign successfully`
                }));
              } else {
                dispatch(setMessage({
                  type: 'error',
                  text: response.data.message || 'Failed to unassign phone from SMS campaign'
                }));
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message
                || err.message
                || 'Failed to unassign phone from SMS campaign';

              dispatch(setMessage({ type: 'error', text: errorMessage }));
              throw err;
            }
          }}
        />
      )}

      {/* Verified Caller IDs Tab Content */}
      {activeSubTab === 'verifiedcallerid' && (
        <VerifiedCallerIdList
          userId={userId}
          onLoadVerifiedCallerIds={async (params) => {
            try {
              const response = await axios.get(`${config.apiBaseURL2}verified-caller-id/list`);
              const allCallerIds = response.data?.data || [];

              const callerIds = allCallerIds.map(item => ({
                ...item,
                id: item.id || item.phone_number,
                phone_number: item.phone_number || item.twilio_phone,
              }));

              setVerifiedCallerIds(allCallerIds);

              return {
                callerIds: callerIds,
                totalCount: callerIds.length,
              };
            } catch (err) {
              console.error('Error loading verified caller IDs:', err);
              return {
                callerIds: [],
                totalCount: 0,
              };
            }
          }}
          onDeleteVerifiedCallerId={async (callerId) => {
            try {
              const id = callerId.id;
              const response = await axios.delete(`${config.apiBaseURL2}verified-caller-id/delete/${id}`);

              if (response.data?.success) {
                dispatch(setMessage({ type: 'success', text: response.data.message || `Verified caller ID ${callerId.phone_number} deleted successfully` }));
              } else {
                dispatch(setMessage({ type: 'error', text: response.data?.message || 'Failed to delete verified caller ID' }));
              }
            } catch (err) {
              dispatch(setMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'Failed to delete verified caller ID'
              }));
              throw err;
            }
          }}
          onCheckVerifiedCallerIdStatus={async (callerId) => {
            // Status is now shown in a modal; reload the list to reflect any changes
          }}
        />
      )}


    </div>
  );
};

export default PhoneInventoryTab;
